/* ============================================================================
   Sneak Freak — theme.js (core runtime)  ·  AphCom  ·  v1.0.0
   ----------------------------------------------------------------------------
   Loaded once by layout/theme.liquid (deferred) AFTER snippets/theme-strings.liquid
   has set `window.theme` (strings, moneyFormat, currency, routes, settings,
   locale, rtl, designMode). This file EXTENDS that object — never replaces it.
   Vanilla ES2020, no build step, no dependencies. Nothing else is exported
   globally except `window.theme`.

   Public interface (CONTRACT §5) — see docs/build/theme-js-notes.md
   -------------------------------------------------------------------------
   theme.pubsub.subscribe(evt, fn) → unsubscribe · theme.pubsub.publish(evt, payload)
   theme.money(cents[, format]) · theme.debounce(fn, ms) · theme.escapeHtml(s)
   theme.fetchConfig(type) · theme.fetchText(url, opts) · theme.fetchJSON(url, opts)
   theme.getSections(url, ids) · theme.sectionHtml(html, id[, {inner}])
   theme.swap(targetEl, sourceEl) · theme.morphById(container, html, ids)
   theme.focusTrap.activate(el, {initial}) → release()
   theme.scrollLock.lock(key) / unlock(key)
   theme.toast(message, {timeout, type})
   theme.storage.get/set/remove (localStorage, JSON, prefix 'sf:')
   theme.cart.add/change/update/get   → publishes cart:updated / cart:error
   theme.favourites.list/has/toggle/count → publishes favourites:change
   theme.quickAdd.open(handle, opener) / close()
   theme.initReveal(root) · theme.initTitleMasks(root)

   Pubsub events: theme:ready, cart:updated, cart:error, drawer:open, drawer:close,
   favourites:change, header:height, locale:change, quickadd:open, remind:open.
   DOM CustomEvents: tabs:change (sf-tabs), remind:open (document), countdown:live.

   Custom elements: sf-drawer, sf-modal, sf-toast, sf-rail, sf-tabs, sf-accordion,
   sf-header, sf-announcement, sf-localization-form, sf-quantity, sf-cart-count,
   sf-favourite-button, sf-quick-add-button, sf-sizes-toggle, sf-remind-button,
   sf-share-button, sf-countdown.
   ============================================================================ */

/* The whole file runs in ONE strict block so its const/class bindings never leak into the
   global lexical scope (feature files may declare their own `const theme = window.theme`). */
'use strict';
{
/* ---------------------------------------------------------------------------
   0. Bootstrap — merge defaults UNDER whatever theme-strings.liquid provided
   --------------------------------------------------------------------------- */
const theme = (window.theme = window.theme || {});

theme.version = '1.0.0';

theme.strings = Object.assign(
  {
    added_to_bag: 'Added to your bag',
    saved_to_favourites: 'Saved to favourites',
    removed_from_favourites: 'Removed from favourites',
    reminder_saved: 'Reminder set',
    copied: 'Link copied',
    error_generic: 'Something went wrong. Please try again.',
    loading: 'Loading…',
    close: 'Close'
  },
  theme.strings || {}
);

theme.moneyFormat = theme.moneyFormat || '${{amount}}';
theme.currency = theme.currency || 'USD';

/* Whose submission is this? Shopify marks EVERY customer form on the page as posted (form.posted_successfully? and
   form.errors are one result shared by all of them) after any single one is sent, and this theme always renders two:
   the footer newsletter and the drop "Remind me" sheet. So each customer form records itself as it submits, and the
   page that comes back can ask whether the result is really its own. The anchor Shopify keeps across the redirect
   (each form posts to /contact#<its id>) answers when storage is blocked; with neither, the answer is null and
   callers keep their old behaviour. */
(function customerPostOwner() {
  const KEY = 'sf-customer-form';
  const MAX_AGE = 10 * 60 * 1000;
  /* only the theme's own customer forms: a bot-protection challenge re-posts through a form of its own, and that
     must not overwrite which of ours was really sent */
  const OURS = ['footer-newsletter', 'notify-form'];
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || OURS.indexOf(form.id) === -1) return;
    const type = form.querySelector('input[name="form_type"]');
    if (!type || type.value !== 'customer') return;
    try { sessionStorage.setItem(KEY, JSON.stringify({ id: form.id, t: Date.now() })); } catch (err) { /* the anchor still answers */ }
  }, true);
  /* The record is spent only by a page that actually shows a customer-form outcome. The challenge page in between
     runs this file too, and spending it there left the real result page with nothing to go on. */
  const showsOutcome = !!document.querySelector('[data-notify-posted], [data-notify-error], [data-news-outcome]')
    || /[?&]customer_posted=true(&|$)/.test(window.location.search);
  let owner = null;
  let anchored = null;
  if (window.location.hash.length > 1) {
    try { anchored = decodeURIComponent(window.location.hash.slice(1)); } catch (err) { anchored = null; }
    if (OURS.indexOf(anchored) === -1) anchored = null;
  }
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) {
      const rec = JSON.parse(raw);
      const fresh = rec && rec.id && Date.now() - rec.t < MAX_AGE;
      if (fresh && !anchored) owner = String(rec.id);
      /* a fresh record means THIS load is the one the submission produced; a reload or a revisit of the same URL is not */
      /* and only when this page was reached by a real navigation: a submit that never left the page (a bot check
         dismissed) leaves a fresh record behind, and a reload or Back to an older success URL must not claim it */
      let navType = 'navigate';
      try { const nav = performance.getEntriesByType('navigation')[0]; if (nav) navType = nav.type; } catch (err) { /* old browser: treat as a navigation */ }
      if (fresh && navType === 'navigate') theme.customerPostRecord = String(rec.id);
      /* read once on the result page: a later reload of it is an ordinary view, not a fresh result */
      if (showsOutcome || !fresh) sessionStorage.removeItem(KEY);
    }
  } catch (err) { owner = null; }
  /* the anchor counts only when it names one of this page's customer forms — #reviews says nothing about who posted */
  if (anchored) owner = anchored;
  if (!owner && window.location.hash.length > 1) {
    let id = null;
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (err) { id = null; }
    const el = id ? document.getElementById(id) : null;
    const type = el && el.tagName === 'FORM' ? el.querySelector('input[name="form_type"]') : null;
    if (type && type.value === 'customer') owner = id;
  }
  theme.customerPostedBy = owner;
  /** @returns {boolean|null} true: that form's own submission · false: another form's · null: cannot tell */
  theme.ownsCustomerPost = (id) => (owner == null ? null : owner === id);
})();

theme.routes = Object.assign(
  {
    root: '/',
    cart: '/cart',
    cartAdd: '/cart/add',
    cartChange: '/cart/change',
    cartUpdate: '/cart/update',
    cartClear: '/cart/clear',
    search: '/search',
    predictiveSearch: '/search/suggest',
    recommendations: '/recommendations/products',
    account: '/account',
    login: '/account/login',
    register: '/account/register',
    logout: '/account/logout'
  },
  theme.routes || {}
);

theme.settings = Object.assign(
  {
    cartType: 'toast',
    favouritesEnabled: true,
    predictiveSearch: true,
    lowStock: 3,
    weekStart: 'sunday',
    timezone: 'Asia/Beirut',
    sizeUnit: 'EU'
  },
  theme.settings || {}
);
theme.settings.motion = Object.assign(
  { reveal: true, writeOn: true, masks: true, cardLift: true, marquees: true },
  (theme.settings && theme.settings.motion) || {}
);

theme.locale = theme.locale || (document.documentElement.lang || 'en').split('-')[0];
theme.rtl = typeof theme.rtl === 'boolean' ? theme.rtl : document.documentElement.dir === 'rtl';
theme.designMode =
  typeof theme.designMode === 'boolean'
    ? theme.designMode
    : !!(window.Shopify && window.Shopify.designMode === true);

const motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
theme.reducedMotion = !!(motionQuery && motionQuery.matches);

/** Durations (ms) — mirror the CSS tokens; 0 under reduced motion. */
const DUR = {
  fast: () => (theme.reducedMotion ? 0 : 200),
  base: () => (theme.reducedMotion ? 0 : 450),
  swap: () => (theme.reducedMotion ? 0 : 260)
};
const EASE_OUT = 'cubic-bezier(.22,1,.36,1)';
const STAGGER_MS = 70;

function syncMotionClass() {
  document.documentElement.classList.toggle('motion-ok', !theme.reducedMotion);
}
if (motionQuery && motionQuery.addEventListener) {
  motionQuery.addEventListener('change', () => {
    theme.reducedMotion = motionQuery.matches;
    syncMotionClass();
  });
}
syncMotionClass();

/* ---------------------------------------------------------------------------
   1. Pubsub
   --------------------------------------------------------------------------- */
const topics = new Map();

theme.pubsub = {
  /**
   * Subscribe to a topic.
   * @param {string} evt topic name, e.g. 'cart:updated'
   * @param {(payload:any)=>void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(evt, fn) {
    if (!topics.has(evt)) topics.set(evt, new Set());
    topics.get(evt).add(fn);
    return () => {
      const set = topics.get(evt);
      if (set) set.delete(fn);
    };
  },
  /**
   * Publish a payload to every subscriber (errors are isolated per subscriber).
   * @param {string} evt
   * @param {any} payload
   */
  publish(evt, payload) {
    const set = topics.get(evt);
    if (!set) return;
    Array.from(set).forEach((fn) => {
      try {
        fn(payload);
      } catch (err) {
        console.error('[theme.pubsub] ' + evt, err);
      }
    });
  }
};

/* ---------------------------------------------------------------------------
   2. Small utilities
   --------------------------------------------------------------------------- */

/**
 * Debounce. Returned function has `.cancel()`.
 * @param {Function} fn
 * @param {number} [ms=200]
 */
theme.debounce = function debounce(fn, ms = 200) {
  let timer = null;
  const wrapped = function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, ms);
  };
  wrapped.cancel = () => {
    clearTimeout(timer);
    timer = null;
  };
  return wrapped;
};

/**
 * Escape a string for safe insertion into HTML.
 * @param {*} s
 * @returns {string}
 */
theme.escapeHtml = function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[c]);
};

/**
 * Format subunits with the shop money format (used ONLY by the free-shipping meter).
 * Supports every Shopify `{{amount*}}` placeholder.
 * @param {number|string} cents
 * @param {string} [format=theme.moneyFormat]
 * @returns {string}
 */
theme.money = function money(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const fmt = format || theme.moneyFormat || '${{amount}}';
  const match = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
  const key = match ? match[1] : 'amount';
  const number = Number(cents);

  const withDelimiters = (n, precision = 2, thousands = ',', decimal = '.') => {
    if (!Number.isFinite(n)) return '0';
    const fixed = (n / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  };

  let value;
  switch (key) {
    case 'amount_no_decimals':
      value = withDelimiters(number, 0);
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(number, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(number, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(number, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(number, 0, ' ', '');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(number, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = withDelimiters(number, 2, ' ', '.');
      break;
    default:
      value = withDelimiters(number, 2, ',', '.');
  }
  return match ? fmt.replace(match[0], value) : value;
};

/** Root prefix without trailing slash ('' or '/ar'). */
function rootPrefix() {
  const root = theme.routes.root || '/';
  return root === '/' ? '' : root.replace(/\/$/, '');
}

/** Append query params to a url that may already carry a query string. */
function withQuery(url, params) {
  const pairs = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
  if (!pairs.length) return url;
  return url + (url.includes('?') ? '&' : '?') + pairs.join('&');
}

/* ---------------------------------------------------------------------------
   3. Fetch helpers + Section Rendering helpers
   --------------------------------------------------------------------------- */

/**
 * Request init for Shopify JSON endpoints.
 * @param {'json'|'javascript'|'text'} [type='json']
 * @returns {RequestInit}
 */
theme.fetchConfig = function fetchConfig(type = 'json') {
  const accept =
    type === 'javascript' ? 'application/javascript' : type === 'text' ? 'text/html' : 'application/json';
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: accept }
  };
};

function httpError(res, body) {
  const err = new Error('HTTP ' + res.status);
  err.status = res.status;
  err.body = body;
  return err;
}

/**
 * fetch → text (throws on !ok with err.status / err.body).
 * @param {string} url
 * @param {RequestInit} [opts]
 * @returns {Promise<string>}
 */
theme.fetchText = async function fetchText(url, opts) {
  const res = await fetch(url, opts);
  const text = await res.text();
  if (!res.ok) throw httpError(res, text);
  return text;
};

/**
 * fetch → JSON (throws on !ok with err.status / err.body already parsed when possible).
 * @param {string} url
 * @param {RequestInit} [opts]
 * @returns {Promise<any>}
 */
theme.fetchJSON = async function fetchJSON(url, opts) {
  const res = await fetch(url, Object.assign({ headers: { Accept: 'application/json' } }, opts || {}));
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = text;
  }
  if (!res.ok) throw httpError(res, data);
  return data;
};

/**
 * Section Rendering API: fetch several sections at once.
 * @param {string} url page url (context for the render)
 * @param {string[]} ids section ids (max 5 per Shopify)
 * @returns {Promise<Record<string,string>>} {id: html}
 */
theme.getSections = function getSections(url, ids) {
  return theme.fetchJSON(withQuery(url, { sections: (ids || []).join(',') }));
};

function parseHtml(html) {
  return new DOMParser().parseFromString(String(html), 'text/html');
}

/**
 * Parse a section-rendering response.
 * @param {string} html
 * @param {string} [id] section id → looks for `#shopify-section-<id>`
 * @param {{inner?:boolean}} [opts] inner:true returns the wrapper's first element child
 * @returns {Element|null} the wrapper (or, when absent, the parsed body's first element child)
 */
theme.sectionHtml = function sectionHtml(html, id, opts = {}) {
  const doc = parseHtml(html);
  const wrapper = id ? doc.getElementById('shopify-section-' + id) : null;
  const root = wrapper || doc.body.firstElementChild;
  if (!root) return null;
  return opts.inner && root.firstElementChild ? root.firstElementChild : root;
};

/**
 * Replace target's children with source's children and mirror source's attributes onto target.
 * Note: `<script>` tags inside parsed HTML are inert.
 * @param {Element} target
 * @param {Element} source
 * @returns {Element} target
 */
theme.swap = function swap(target, source) {
  if (!target || !source) return target;
  Array.from(target.attributes).forEach((a) => {
    if (!source.hasAttribute(a.name)) target.removeAttribute(a.name);
  });
  Array.from(source.attributes).forEach((a) => target.setAttribute(a.name, a.value));
  target.replaceChildren(...Array.from(source.childNodes));
  return target;
};

/**
 * Swap elements by id from an HTML string into a live container.
 * @param {Element} container live root to search in
 * @param {string|Document|Element} html new markup
 * @param {string[]} [ids] ids to morph; defaults to [container.id] (or the whole container)
 * @returns {string[]} ids actually swapped
 */
theme.morphById = function morphById(container, html, ids) {
  if (!container) return [];
  const src = typeof html === 'string' ? parseHtml(html) : html;
  const find = (root, id) =>
    root.getElementById ? root.getElementById(id) : root.querySelector('#' + CSS.escape(id));
  const list = ids && ids.length ? ids : container.id ? [container.id] : [];
  if (!list.length) {
    const root = src.body ? src.body.firstElementChild : src;
    if (root) theme.swap(container, root);
    return root ? ['*'] : [];
  }
  const done = [];
  list.forEach((id) => {
    const from = find(src, id);
    const to = container.id === id ? container : find(container, id);
    if (from && to) {
      theme.swap(to, from);
      done.push(id);
    }
  });
  return done;
};

/* ---------------------------------------------------------------------------
   4. Focus trap
   --------------------------------------------------------------------------- */
const FOCUSABLE =
  'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

function isVisible(el) {
  return !el.hidden && !el.closest('[hidden]') && el.getClientRects().length > 0;
}

function focusables(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(isVisible);
}

function safeFocus(el) {
  if (!el || typeof el.focus !== 'function') return;
  try {
    el.focus({ preventScroll: true });
  } catch (e) {
    el.focus();
  }
}

const trapStack = [];

theme.focusTrap = {
  /**
   * Trap Tab / Shift+Tab inside `el`, move focus in, and return a release function
   * that restores focus to the previously focused element (the opener).
   * @param {Element} el
   * @param {{initial?: Element|string, returnTo?: Element}} [opts]
   * @returns {() => void} release
   */
  activate(el, opts = {}) {
    const previous = opts.returnTo || document.activeElement;
    const ctrl = new AbortController();
    const trap = { el, previous, ctrl };
    trapStack.push(trap);

    const isTop = () => trapStack[trapStack.length - 1] === trap;

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Tab' || !isTop()) return;
        const list = focusables(el);
        if (!list.length) {
          e.preventDefault();
          safeFocus(el);
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !el.contains(active))) {
          e.preventDefault();
          safeFocus(last);
        } else if (!e.shiftKey && (active === last || !el.contains(active))) {
          e.preventDefault();
          safeFocus(first);
        }
      },
      { signal: ctrl.signal }
    );

    document.addEventListener(
      'focusin',
      (e) => {
        if (!isTop() || el.contains(e.target)) return;
        const list = focusables(el);
        safeFocus(list[0] || el);
      },
      { signal: ctrl.signal }
    );

    let initial = opts.initial;
    if (typeof initial === 'string') initial = el.querySelector(initial);
    if (!initial) initial = focusables(el)[0];
    if (!initial) {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      initial = el;
    }
    safeFocus(initial);

    let released = false;
    return function release() {
      if (released) return;
      released = true;
      ctrl.abort();
      const i = trapStack.indexOf(trap);
      if (i > -1) trapStack.splice(i, 1);
      if (previous && document.contains(previous)) safeFocus(previous);
    };
  }
};

/* ---------------------------------------------------------------------------
   5. Scroll lock (counted keys, scrollbar compensation via --scrollbar-w)
   --------------------------------------------------------------------------- */
const locks = new Set();

theme.scrollLock = {
  /**
   * Lock document scrolling for `key`. Adds `html.scroll-locked` and sets `--scrollbar-w`.
   * @param {string} [key='default']
   */
  lock(key = 'default') {
    if (!locks.size) {
      const html = document.documentElement;
      const sw = Math.max(0, window.innerWidth - html.clientWidth);
      html.style.setProperty('--scrollbar-w', sw + 'px');
      html.classList.add('scroll-locked');
    }
    locks.add(key);
  },
  /**
   * Release the lock for `key`; scrolling resumes when no keys remain.
   * @param {string} [key='default']
   */
  unlock(key = 'default') {
    locks.delete(key);
    if (!locks.size) {
      const html = document.documentElement;
      html.classList.remove('scroll-locked');
      html.style.setProperty('--scrollbar-w', '0px');
    }
  },
  /** @returns {boolean} */
  get locked() {
    return locks.size > 0;
  }
};

/* ---------------------------------------------------------------------------
   6. Storage (localStorage, JSON, prefix 'sf:')
   --------------------------------------------------------------------------- */
const STORAGE_PREFIX = 'sf:';
const storageKey = (k) => (String(k).startsWith(STORAGE_PREFIX) ? String(k) : STORAGE_PREFIX + k);

theme.storage = {
  /**
   * @param {string} key without prefix ('favourites' → 'sf:favourites')
   * @param {*} [fallback]
   */
  get(key, fallback) {
    try {
      const raw = window.localStorage.getItem(storageKey(key));
      return raw == null ? fallback : JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  },
  /** @param {string} key @param {*} val JSON-serialisable */
  set(key, val) {
    try {
      window.localStorage.setItem(storageKey(key), JSON.stringify(val));
      return true;
    } catch (e) {
      return false;
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      window.localStorage.removeItem(storageKey(key));
    } catch (e) {
      /* ignore */
    }
  }
};

/* ---------------------------------------------------------------------------
   7. Toast — single <sf-toast id="sf-toast"> created lazily; queued messages
   --------------------------------------------------------------------------- */
class SFToast extends HTMLElement {
  constructor() {
    super();
    this._queue = [];
    this._busy = false;
    this._timer = null;
  }

  connectedCallback() {
    this.classList.add('sf-toast');
    if (!this.hasAttribute('role')) this.setAttribute('role', 'status');
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    this._queue = [];
    this._busy = false;
  }

  /**
   * Show a message (queued if one is visible).
   * @param {string} message
   * @param {{timeout?:number, type?:'info'|'success'|'error', tone?:'error'}} [opts]
   */
  show(message, opts = {}) {
    const msg = String(message == null ? '' : message);
    if (!msg) return;
    const last = this._queue[this._queue.length - 1];
    if (last && last.message === msg) return;
    if (this._queue.length >= 3) this._queue.shift();
    this._queue.push({ message: msg, timeout: opts.timeout || 2200, type: opts.type || 'info', tone: opts.tone || '' });
    if (!this._busy) this._next();
  }

  _next() {
    const item = this._queue.shift();
    if (!item) {
      this._busy = false;
      return;
    }
    this._busy = true;
    this.textContent = item.message;
    this.dataset.type = item.type;
    /* opts.tone 'error' paints the pill in the error colour (theme.css .sf-toast--error). It is opt-in, separate from
       type, so only the product form's failed add (request 4) asks for it and every other toast keeps the ink pill */
    this.classList.toggle('sf-toast--error', item.tone === 'error');
    this.hidden = false;
    // Two frames so the transition runs from the closed state.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this.classList.remove('is-open');
      this._timer = setTimeout(() => this._next(), DUR.fast() + 40);
    }, item.timeout);
  }
}
if (!customElements.get('sf-toast')) customElements.define('sf-toast', SFToast);

function toastElement() {
  let el = document.getElementById('sf-toast');
  if (!el) {
    el = document.createElement('sf-toast');
    el.id = 'sf-toast';
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Show a transient status message.
 * @param {string} message
 * @param {{timeout?:number, type?:string}} [opts]
 */
theme.toast = function toast(message, opts = {}) {
  const el = toastElement();
  if (typeof el.show === 'function') el.show(message, opts);
  else el.textContent = String(message);
};

/* ---------------------------------------------------------------------------
   8. Cart (light) — add/change/update/get with sections, stale guard, errors
   --------------------------------------------------------------------------- */
const cartState = {
  seq: 0, // monotonically increasing request id
  latest: { add: 0, change: 0, update: 0, get: 0 },
  controllers: { add: null, change: null, update: null, get: null },
  lastPublished: 0
};

function cartUrl(route) {
  const url = theme.routes[route] || '';
  return /\.js$/.test(url) ? url : url + '.js';
}

function cartGetUrl() {
  if (theme.routes.cartGet) return theme.routes.cartGet;
  return cartUrl('cart');
}

function sectionsParam(sections) {
  if (!sections) return undefined;
  return Array.isArray(sections) ? sections.join(',') : String(sections);
}

function normalizeItems(items) {
  const list = Array.isArray(items) ? items : [items];
  return list
    .filter((it) => it != null)
    .map((it) => {
      if (typeof it === 'number' || typeof it === 'string') return { id: Number(it), quantity: 1 };
      const out = Object.assign({ quantity: 1 }, it);
      if (out.id != null) out.id = Number(out.id);
      return out;
    });
}

function publishCartError(source, status, data, item) {
  const payload = {
    source,
    status: status || 0,
    message: (data && data.message) || theme.strings.error_generic,
    description: (data && data.description) || (data && data.message) || theme.strings.error_generic,
    errors: data && data.errors,
    item
  };
  theme.pubsub.publish('cart:error', payload);
  const err = new Error(payload.description);
  Object.assign(err, payload);
  return err;
}

/**
 * Perform one cart request with stale-response guarding.
 * `abortPrevious` aborts an in-flight request of the same method.
 */
async function cartRequest(source, url, body, { abortPrevious = false, item } = {}) {
  const id = ++cartState.seq;
  cartState.latest[source] = id;

  if (abortPrevious && cartState.controllers[source]) cartState.controllers[source].abort();
  const ctrl = new AbortController();
  cartState.controllers[source] = ctrl;

  const init = body ? theme.fetchConfig('json') : { method: 'GET', headers: { Accept: 'application/json' } };
  if (body) init.body = JSON.stringify(body);
  init.signal = ctrl.signal;

  let res;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    throw publishCartError(source, 0, null, item);
  } finally {
    if (cartState.controllers[source] === ctrl) cartState.controllers[source] = null;
  }

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = null;
  }
  if (!res.ok) throw publishCartError(source, res.status, data, item);

  return { id, data, stale: id < cartState.latest[source] };
}

function publishCart(id, source, cart, sections, extra) {
  if (id < cartState.lastPublished) return false;
  cartState.lastPublished = id;
  theme.pubsub.publish('cart:updated', Object.assign({ cart, sections: sections || null, source }, extra || {}));
  return true;
}

theme.cart = {
  /**
   * Add one or many items. Always posts `{items:[…]}`; publishes cart:updated with the FULL cart
   * (a follow-up GET /cart.js runs when add.js only returns the items).
   * `opts.onAdded({items, sections})` runs the moment Shopify has accepted the add (add.js 200), BEFORE that
   * follow-up read — so a caller can confirm the add to the shopper without waiting a second round trip that only
   * exists for the bag count (request 4: ~200ms on a good connection, ~600ms on 3G). cart:updated and the returned
   * promise are unchanged.
   * @param {number|string|object|Array} items variant id, {id, quantity, properties, selling_plan} or an array
   * @param {{sections?: string[]|string, sectionsUrl?: string, onAdded?: function}} [opts]
   * @returns {Promise<{cart:object, items:object[], sections:object|null}>}
   */
  async add(items, opts = {}) {
    const list = normalizeItems(items);
    const body = { items: list };
    const sections = sectionsParam(opts.sections);
    if (sections) {
      body.sections = sections;
      body.sections_url = opts.sectionsUrl || window.location.pathname;
    }
    const { id, data } = await cartRequest('add', cartUrl('cartAdd'), body, { item: list[0] });
    const added = data && Array.isArray(data.items) ? data.items : data ? [data] : [];
    let cart = data && typeof data.item_count === 'number' ? data : null;
    const responseSections = (data && data.sections) || null;
    if (typeof opts.onAdded === 'function') {
      try { opts.onAdded({ items: added, sections: responseSections }); } catch (e) { /* the caller's UI never stops the cart update */ }
    }
    if (!cart) {
      try {
        const res = await fetch(cartGetUrl(), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cart.js ' + res.status);
        cart = await res.json();
      } catch (err) {
        throw publishCartError('add', 0, null, list[0]);
      }
    }
    publishCart(id, 'add', cart, responseSections, { items: added });
    return { cart, items: added, sections: responseSections };
  },

  /**
   * Change a line quantity (only `id` + `quantity` are sent; properties are immutable).
   * Aborts a previous in-flight change.
   * @param {string|number} key line key (or variant id)
   * @param {number} quantity
   * @param {{sections?: string[]|string, sectionsUrl?: string}} [opts]
   * @returns {Promise<object>} full cart JSON
   */
  async change(key, quantity, opts = {}) {
    const body = { id: key, quantity: Number(quantity) };
    const sections = sectionsParam(opts.sections);
    if (sections) {
      body.sections = sections;
      body.sections_url = opts.sectionsUrl || window.location.pathname;
    }
    const { id, data } = await cartRequest('change', cartUrl('cartChange'), body, { abortPrevious: true });
    publishCart(id, 'change', data, data && data.sections, { key, quantity: body.quantity });
    return data;
  },

  /**
   * POST /cart/update.js with `{updates, note, attributes, discount}` as given.
   * Aborts a previous in-flight update.
   * @param {object} payload
   * @param {{sections?: string[]|string, sectionsUrl?: string}} [opts]
   * @returns {Promise<object>} full cart JSON
   */
  async update(payload, opts = {}) {
    const body = Object.assign({}, payload || {});
    const sections = sectionsParam(opts.sections);
    if (sections) {
      body.sections = sections;
      body.sections_url = opts.sectionsUrl || window.location.pathname;
    }
    const { id, data } = await cartRequest('update', cartUrl('cartUpdate'), body, { abortPrevious: true });
    publishCart(id, 'update', data, data && data.sections);
    return data;
  },

  /**
   * GET /cart.js. Publishes cart:updated {source:'get'} so counters can resync.
   * @returns {Promise<object>} full cart JSON
   */
  async get() {
    const { id, data } = await cartRequest('get', cartGetUrl(), null, { abortPrevious: true });
    publishCart(id, 'get', data, null);
    return data;
  }
};

/* ---------------------------------------------------------------------------
   9. Favourites — device-local, storage key 'sf:favourites'
   --------------------------------------------------------------------------- */
const FAV_KEY = 'favourites';

function readFavourites() {
  const list = theme.storage.get(FAV_KEY, []);
  return Array.isArray(list) ? list.filter((e) => e && typeof e.handle === 'string' && e.handle) : [];
}

function syncFavouriteButtons(handle) {
  const saved = new Set(readFavourites().map((e) => e.handle));
  const sel = handle
    ? 'sf-favourite-button[data-handle="' + CSS.escape(handle) + '"]'
    : 'sf-favourite-button[data-handle]';
  document.querySelectorAll(sel).forEach((el) => {
    if (typeof el.setSaved === 'function') el.setSaved(saved.has(el.dataset.handle));
  });
}

theme.favourites = {
  /** @returns {Array<{handle:string,title?:string,vendor?:string,url?:string,image?:string,price?:string}>} */
  list() {
    return readFavourites();
  },
  /** @param {string} handle */
  has(handle) {
    return readFavourites().some((e) => e.handle === handle);
  },
  /** @returns {number} */
  count() {
    return readFavourites().length;
  },
  /**
   * Toggle a product. Publishes favourites:change {handles, handle, saved, list} and
   * syncs every sf-favourite-button[data-handle] on the page.
   * @param {string} handle
   * @param {{title?:string,vendor?:string,url?:string,image?:string,price?:string}} [meta]
   * @param {{silent?:boolean}} [opts] silent → no toast
   * @returns {boolean} saved state after the toggle
   */
  toggle(handle, meta = {}, opts = {}) {
    if (!handle) return false;
    let list = readFavourites();
    const saved = !list.some((e) => e.handle === handle);
    if (saved) {
      list.push({
        handle,
        title: meta.title || '',
        vendor: meta.vendor || '',
        url: meta.url || rootPrefix() + '/products/' + handle,
        image: meta.image || '',
        price: meta.price || ''
      });
    } else {
      list = list.filter((e) => e.handle !== handle);
    }
    theme.storage.set(FAV_KEY, list);
    syncFavouriteButtons(handle);
    theme.pubsub.publish('favourites:change', {
      handles: list.map((e) => e.handle),
      handle,
      saved,
      list
    });
    if (!opts.silent) {
      theme.toast(saved ? theme.strings.saved_to_favourites : theme.strings.removed_from_favourites, {
        type: 'success'
      });
    }
    return saved;
  },
  /**
   * Remove a product without toasting.
   * @param {string} handle
   */
  remove(handle) {
    if (this.has(handle)) this.toggle(handle, {}, { silent: true });
  }
};

window.addEventListener('storage', (e) => {
  if (e.key === storageKey(FAV_KEY)) {
    syncFavouriteButtons();
    theme.pubsub.publish('favourites:change', {
      handles: readFavourites().map((x) => x.handle),
      handle: null,
      saved: null,
      list: readFavourites(),
      external: true
    });
  }
});

/* ---------------------------------------------------------------------------
   10. sf-drawer / sf-modal (CONTRACT §6.11)
   --------------------------------------------------------------------------- */
const openDrawers = []; // stack of open sf-drawer elements (topmost last)
/* Any open sheet marks the document, so fixed chrome that would otherwise sit over it can stand down.
   Driven off the stack rather than a single drawer, so nested sheets behave. */
function syncSheetState() {
  document.documentElement.classList.toggle('sf-sheet-open', openDrawers.length > 0);
}

class SFDrawer extends HTMLElement {
  constructor() {
    super();
    this._release = null;
    this._opener = null;
    this._timer = null;
    this._ctrl = null;
  }

  static get observedAttributes() {
    return ['data-position'];
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get position() {
    return this.dataset.position || this.defaultPosition;
  }

  get defaultPosition() {
    return 'bottom';
  }

  get lockScroll() {
    return this.dataset.lockScroll !== 'false';
  }

  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    if (!this.dataset.position) this.dataset.position = this.defaultPosition;
    this.classList.add('drawer', 'drawer--' + this.position);
    if (!this.isOpen && !this.hasAttribute('hidden')) this.hidden = true;

    this.addEventListener(
      'click',
      (e) => {
        const closer = e.target.closest('[data-drawer-close]');
        if (closer && this.contains(closer) && closer.closest('sf-drawer, sf-modal') === this) {
          e.preventDefault();
          this.close();
        }
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.isOpen && openDrawers[openDrawers.length - 1] === this) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
      },
      { signal }
    );

    if (theme.designMode) {
      document.addEventListener(
        'shopify:section:select',
        (e) => {
          if (this.hasAttribute('data-editor-open') && e.target && e.target.contains(this)) this.open();
        },
        { signal }
      );
      document.addEventListener(
        'shopify:section:deselect',
        (e) => {
          if (this.hasAttribute('data-editor-open') && e.target && e.target.contains(this)) this.close();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    clearTimeout(this._timer);
    this._teardownOpenState();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'data-position' && oldVal !== newVal && this.isConnected) {
      if (oldVal) this.classList.remove('drawer--' + oldVal);
      this.classList.add('drawer--' + (newVal || this.defaultPosition));
    }
  }

  /**
   * Open the drawer. Focus is trapped in `.drawer__panel` and returned to `opener` on close.
   * @param {Element} [opener] element that triggered the open (focus returns there)
   */
  open(opener) {
    if (this.isOpen) return;
    clearTimeout(this._timer);
    cancelAnimationFrame(this._raf);
    /* re-opening mid-exit: drop the closing state so the two cannot fight */
    this.classList.remove('is-closing');
    this._opener = opener || document.activeElement;
    this.hidden = false;
    if (this.lockScroll) theme.scrollLock.lock('drawer:' + (this.id || 'anon'));
    openDrawers.push(this);
    syncSheetState();

    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) {
      opener.setAttribute('aria-expanded', 'true');
    }

    const panel = this.querySelector('.drawer__panel') || this;
    const initial = this.querySelector('[data-drawer-initial]') || this.querySelector('.drawer__close');
    if (this.dataset.focusTrap !== 'false') {
      this._release = theme.focusTrap.activate(panel, { initial, returnTo: this._opener });
    } else {
      safeFocus(initial || panel);
    }

    /* Two animation frames rather than a 10ms timer: the browser is guaranteed to have painted the closed
       state before .is-open lands, so the entrance cannot snap when the main thread is busy. */
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => {
      this._raf = requestAnimationFrame(() => this.classList.add('is-open'));
    });
    theme.pubsub.publish('drawer:open', { id: this.id, el: this, opener: this._opener });
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id: this.id } }));
  }

  /** Close the drawer (waits for the exit motion before setting `hidden`). */
  close() {
    if (!this.isOpen && this.hidden) return;
    clearTimeout(this._timer);
    cancelAnimationFrame(this._raf);
    this.classList.remove('is-open');
    this.classList.add('is-closing');
    const opener = this._opener;
    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) {
      opener.setAttribute('aria-expanded', 'false');
    }
    this._teardownOpenState();
    /* The panel is removed when its exit actually finishes, not on a fixed guess. transitionend is the
       primary signal; the timer is only a fail-safe for an interrupted or skipped transition. */
    const panel = this.querySelector('.drawer__panel');
    const settle = () => {
      if (this.isOpen) return;              /* re-opened while closing — leave it alone */
      this.hidden = true;
      this.classList.remove('is-closing');
    };
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      if (panel) panel.removeEventListener('transitionend', onEnd);
      settle();
    };
    const onEnd = (e) => {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      finish();
    };
    const reduced = !!(theme.reducedMotion) || !document.documentElement.classList.contains('motion-ok');
    if (panel && !reduced) {
      panel.addEventListener('transitionend', onEnd);
      this._timer = setTimeout(finish, DUR.base() + 120);
    } else {
      this._timer = setTimeout(finish, reduced ? 0 : DUR.base());
    }
    theme.pubsub.publish('drawer:close', { id: this.id, el: this });
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id: this.id } }));
  }

  /** @param {Element} [opener] */
  toggle(opener) {
    if (this.isOpen) this.close();
    else this.open(opener);
  }

  _teardownOpenState() {
    const i = openDrawers.indexOf(this);
    if (i > -1) openDrawers.splice(i, 1);
    /* every closing path funnels through here — the close button, the backdrop, Escape, a submit that
       closes the sheet, and disconnectedCallback when a section re-render removes the drawer outright —
       so the dock can never be left hidden with nothing on screen to explain it */
    syncSheetState();
    if (this.lockScroll) theme.scrollLock.unlock('drawer:' + (this.id || 'anon'));
    if (this._release) {
      const release = this._release;
      this._release = null;
      release();
    }
    this._opener = null;
  }
}

class SFModal extends SFDrawer {
  get defaultPosition() {
    return 'center';
  }
}

if (!customElements.get('sf-drawer')) customElements.define('sf-drawer', SFDrawer);
if (!customElements.get('sf-modal')) customElements.define('sf-modal', SFModal);

/* Delegated openers `[data-drawer-open="id"]` (document-level, survive section reloads). */
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (!opener) return;
  const id = opener.getAttribute('data-drawer-open');
  const drawer = id && document.getElementById(id);
  if (!drawer || typeof drawer.open !== 'function') return;
  e.preventDefault();
  if (drawer.isOpen && opener.hasAttribute('aria-expanded')) drawer.close();
  else drawer.open(opener);
});

/* Placeholder menu links.
   A mockup menu points unfinished destinations at "#". Left alone the browser treats that as a same-page
   fragment and jumps the view to the top, which reads as a broken link. They are inert instead: no jump, no
   history entry, and the panel or drawer they sit in stays open. (The page transition already ignores "#".) */
const PLACEHOLDER_HREF = new RegExp("^/?#$");
document.addEventListener('click', (e) => {
  const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
  if (!a) return;
  const href = a.getAttribute('href') || '';
  if (!PLACEHOLDER_HREF.test(href.trim())) return;
  e.preventDefault();
}, true);
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
  if (!a) return;
  if (PLACEHOLDER_HREF.test((a.getAttribute('href') || '').trim())) e.preventDefault();
});

/* Global Escape: close the topmost drawer, else collapse open size strips. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  const top = openDrawers[openDrawers.length - 1];
  if (top) {
    top.close();
    return;
  }
  /* Size strips are independent, so Escape collapses only the one holding focus — never every open card. */
  const focused = document.activeElement && document.activeElement.closest
    ? document.activeElement.closest('.card-b, .drop-card')
    : null;
  if (!focused) return;
  focused.querySelectorAll('sf-sizes-toggle.is-open').forEach((el) => {
    if (typeof el.setOpen === 'function') el.setOpen(false);
  });
});

/* ---------------------------------------------------------------------------
   11. sf-rail — horizontal scroller with optional dots, keyboard, snap
   --------------------------------------------------------------------------- */
class SFRail extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    this.classList.add('rail');
    if (this.hasAttribute('data-snap')) this.classList.add('rail--snap');

    this._dots = this.hasAttribute('dots') ? this._resolveDots() : null;
    this._raf = 0;

    this.addEventListener(
      'scroll',
      () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          this._updateDots();
        });
      },
      { passive: true, signal }
    );

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.target !== this) return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1; // physical direction: right = +scrollLeft in both dirs
        this.scrollBy({ left: dir * this._step(), behavior: theme.reducedMotion ? 'auto' : 'smooth' });
      },
      { signal }
    );

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this._measure());
      this._ro.observe(this);
    } else {
      window.addEventListener('resize', theme.debounce(() => this._measure(), 150), { signal });
    }

    if (theme.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (e) => {
          const block = e.target;
          if (!block || !this.contains(block) || block === this) return;
          const child = Array.from(this.children).find((c) => c === block || c.contains(block));
          if (child) this.scrollToChild(child);
        },
        { signal }
      );
    }

    this._measure();
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    if (this._ro) this._ro.disconnect();
    cancelAnimationFrame(this._raf);
    if (this._ownDots && this._dots) this._dots.remove();
  }

  /** @returns {boolean} whether the rail overflows horizontally */
  get overflows() {
    return this.scrollWidth > this.clientWidth + 1;
  }

  /**
   * Scroll so `child` sits at the rail's start edge.
   * @param {Element} child
   * @param {'auto'|'smooth'} [behavior]
   */
  scrollToChild(child, behavior) {
    const rect = child.getBoundingClientRect();
    const mine = this.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(this).paddingInlineStart) || 0;
    const delta = theme.rtl ? rect.right - (mine.right - pad) : rect.left - (mine.left + pad);
    this.scrollBy({ left: delta, behavior: behavior || (theme.reducedMotion ? 'auto' : 'smooth') });
  }

  _step() {
    const first = this.firstElementChild;
    if (!first) return this.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  _resolveDots() {
    const attr = this.getAttribute('dots');
    let dots = null;
    if (attr) dots = document.getElementById(attr);
    if (!dots && this.nextElementSibling && this.nextElementSibling.classList.contains('rail-dots')) {
      dots = this.nextElementSibling;
    }
    if (!dots && this.parentElement) dots = this.parentElement.querySelector(':scope > .rail-dots');
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'rail-dots';
      dots.setAttribute('aria-hidden', 'true');
      Array.from(this.children).forEach(() => dots.appendChild(document.createElement('span')));
      this.insertAdjacentElement('afterend', dots);
      this._ownDots = true;
    }
    return dots;
  }

  _measure() {
    const overflow = this.overflows;
    if (!this.hasAttribute('data-no-tabindex')) {
      if (overflow && !this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      else if (!overflow && this.getAttribute('tabindex') === '0') this.removeAttribute('tabindex');
    }
    if (this._dots) this._dots.hidden = !overflow;
    this._updateDots();
  }

  _updateDots() {
    if (!this._dots) return;
    const dots = Array.from(this._dots.children);
    if (!dots.length) return;
    const max = this.scrollWidth - this.clientWidth;
    const pos = Math.min(1, Math.max(0, max > 0 ? Math.abs(this.scrollLeft) / max : 0));
    let index;
    const kids = Array.from(this.children);
    if (dots.length === kids.length && kids.length) {
      if (pos >= 0.99) index = dots.length - 1;
      else {
        const mine = this.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(this).paddingInlineStart) || 0;
        const edge = theme.rtl ? mine.right - pad : mine.left + pad;
        let best = 0;
        let bestDist = Infinity;
        kids.forEach((k, i) => {
          const r = k.getBoundingClientRect();
          const d = Math.abs((theme.rtl ? r.right : r.left) - edge);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        index = best;
      }
    } else {
      index = Math.round(pos * (dots.length - 1));
    }
    dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }
}
if (!customElements.get('sf-rail')) customElements.define('sf-rail', SFRail);

/* ---------------------------------------------------------------------------
   12. sf-tabs — tablist with roving tabindex and underline indicator
   --------------------------------------------------------------------------- */
let tabsUid = 0;

class SFTabs extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    this._uid = ++tabsUid;

    this.list = this.matches('[role="tablist"]') ? this : this.querySelector('[role="tablist"]') || this;
    if (!this.list.hasAttribute('role')) this.list.setAttribute('role', 'tablist');
    this.tabs = Array.from(this.list.querySelectorAll('[data-tab]'));
    if (!this.tabs.length) return;
    this.indicator = this.querySelector('.tabs-underline__indicator');

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('role', 'tab');
      if (!tab.id) tab.id = 'sf-tab-' + this._uid + '-' + i;
      const panel = this._panelFor(tab.dataset.tab);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        if (!panel.id) panel.id = 'sf-tabpanel-' + this._uid + '-' + i;
        panel.setAttribute('aria-labelledby', tab.id);
        tab.setAttribute('aria-controls', panel.id);
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
      }
    });

    this.list.addEventListener(
      'click',
      (e) => {
        const tab = e.target.closest('[data-tab]');
        if (tab && this.tabs.includes(tab)) this.select(tab.dataset.tab, { focus: false });
      },
      { signal }
    );

    this.list.addEventListener(
      'keydown',
      (e) => {
        const current = this.tabs.indexOf(document.activeElement);
        if (current < 0) return;
        let next = null;
        const forward = theme.rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = theme.rtl ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === forward) next = (current + 1) % this.tabs.length;
        else if (e.key === back) next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = this.tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        this.select(this.tabs[next].dataset.tab, { focus: true });
      },
      { signal }
    );

    const initial =
      this.tabs.find((t) => t.classList.contains('is-active')) ||
      this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') ||
      this.tabs.find((t) => t.dataset.tab === this.getAttribute('data-tab-active')) ||
      this.tabs[0];
    this.select(initial.dataset.tab, { focus: false, silent: true, instant: true });

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this._moveIndicator(true));
      this._ro.observe(this.list);
    } else {
      window.addEventListener('resize', theme.debounce(() => this._moveIndicator(true), 100), { signal });
    }
    /* the edge fades follow the strip's scroll and size in every browser (they sat inside the fallback branch) */
    this.list.addEventListener('scroll', () => this._edges(), { passive: true, signal });
    window.addEventListener('resize', theme.debounce(() => this._edges(), 100), { signal });
    this._edges();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this._moveIndicator(true));
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    if (this._ro) this._ro.disconnect();
  }

  /** @returns {string|null} active tab id */
  get active() {
    const t = this.tabs && this.tabs.find((x) => x.getAttribute('aria-selected') === 'true');
    return t ? t.dataset.tab : null;
  }

  _panelFor(id) {
    if (!id) return null;
    const sel = '[data-tab-panel="' + CSS.escape(id) + '"]';
    const scope = this.closest('[data-tabs-scope]') || this.closest('.shopify-section') || document;
    return this.querySelector(sel) || scope.querySelector(sel);
  }

  /**
   * Activate a tab by its `data-tab` id. Emits `tabs:change` {id, tab, panel}.
   * @param {string} id
   * @param {{focus?:boolean, silent?:boolean, instant?:boolean}} [opts]
   */
  select(id, opts = {}) {
    const tab = this.tabs.find((t) => t.dataset.tab === id);
    if (!tab) return;
    const changed = this.active !== id;
    this.tabs.forEach((t) => {
      const on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
      const panel = this._panelFor(t.dataset.tab);
      if (panel) panel.hidden = !on;
    });
    this.setAttribute('data-tab-active', id);
    if (this.list !== this) this.list.setAttribute('data-tab-active', id);
    if (opts.focus) safeFocus(tab);
    this._moveIndicator(!!opts.instant);
    this._revealTab(tab);
    if (changed && !opts.silent) {
      this.dispatchEvent(
        new CustomEvent('tabs:change', {
          bubbles: true,
          detail: { id, tab, panel: this._panelFor(id) }
        })
      );
    }
  }

  /* FIX 09 — the strip scrolls when there are more tabs than fit. Keep the chosen tab in view and tell the
     CSS which edges still have tabs beyond them, so the fades only show where there is more to reach. */
  _revealTab(tab) {
    const list = this.list;
    if (!list || !tab || list.scrollWidth <= list.clientWidth + 1) { this._edges(); return; }
    const l = list.getBoundingClientRect(), t = tab.getBoundingClientRect();
    if (t.left < l.left + 8 || t.right > l.right - 8) {
      const to = list.scrollLeft + (t.left - l.left) - (l.width - t.width) / 2;
      list.scrollTo({ left: Math.max(0, to), behavior: theme.reducedMotion ? 'auto' : 'smooth' });
    }
    this._edges();
  }

  _edges() {
    const list = this.list;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    const on = max > 1;
    list.classList.toggle('has-overflow', on);
    list.classList.toggle('at-start', !on || list.scrollLeft <= 1);
    list.classList.toggle('at-end', !on || list.scrollLeft >= max - 1);
  }

  _moveIndicator(instant) {
    if (!this.indicator) return;
    const tab = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (!tab) return;
    const cRect = this.list.getBoundingClientRect();
    const bRect = tab.getBoundingClientRect();
    if (!bRect.width) return;
    // Physical offsets: the indicator is anchored `left:0`, which keeps RTL correct.
    const x = bRect.left - cRect.left + this.list.scrollLeft;
    if (instant) {
      const prev = this.indicator.style.transition;
      this.indicator.style.transition = 'none';
      this.indicator.style.transform = 'translateX(' + x + 'px) scaleX(' + bRect.width + ')';
      void this.indicator.offsetWidth;
      this.indicator.style.transition = prev;
    } else {
      this.indicator.style.transform = 'translateX(' + x + 'px) scaleX(' + bRect.width + ')';
    }
  }
}
if (!customElements.get('sf-tabs')) customElements.define('sf-tabs', SFTabs);

/* ---------------------------------------------------------------------------
   13. sf-accordion — <details> enhancement with WAAPI height animation
   --------------------------------------------------------------------------- */
class SFAccordion extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    this._anims = new WeakMap();
    this.addEventListener(
      'click',
      (e) => {
        const summary = e.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS' || details.closest('sf-accordion') !== this) return;
        e.preventDefault();
        this.toggle(details);
      },
      { signal: this._ctrl.signal }
    );
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
  }

  /** @param {HTMLDetailsElement} details */
  toggle(details) {
    if (details.open) this.close(details);
    else this.openItem(details);
  }

  /**
   * Open one `<details>` (closes siblings when `data-single`).
   * @param {HTMLDetailsElement} details
   */
  openItem(details) {
    if (this.hasAttribute('data-single')) {
      this.querySelectorAll('details[open]').forEach((d) => {
        if (d !== details) this.close(d);
      });
    }
    const dur = DUR.base();
    if (!dur || typeof details.animate !== 'function') {
      details.open = true;
      return;
    }
    this._cancel(details);
    const start = details.offsetHeight;
    details.open = true;
    const end = details.offsetHeight;
    details.style.overflow = 'hidden';
    const anim = details.animate({ height: [start + 'px', end + 'px'] }, { duration: dur, easing: EASE_OUT });
    this._anims.set(details, anim);
    anim.onfinish = anim.oncancel = () => {
      details.style.overflow = '';
      this._anims.delete(details);
    };
  }

  /** @param {HTMLDetailsElement} details */
  close(details) {
    const dur = DUR.base();
    if (!dur || typeof details.animate !== 'function') {
      details.open = false;
      return;
    }
    this._cancel(details);
    const summary = details.querySelector(':scope > summary');
    const start = details.offsetHeight;
    const end = summary ? summary.offsetHeight : 0;
    details.style.overflow = 'hidden';
    details.classList.add('is-closing');
    const anim = details.animate({ height: [start + 'px', end + 'px'] }, { duration: dur, easing: EASE_OUT });
    this._anims.set(details, anim);
    anim.onfinish = () => {
      details.open = false;
      details.style.overflow = '';
      details.classList.remove('is-closing');
      this._anims.delete(details);
    };
    anim.oncancel = () => {
      details.style.overflow = '';
      details.classList.remove('is-closing');
      this._anims.delete(details);
    };
  }

  _cancel(details) {
    const running = this._anims.get(details);
    if (running) running.cancel();
  }
}
if (!customElements.get('sf-accordion')) customElements.define('sf-accordion', SFAccordion);

/* ---------------------------------------------------------------------------
   14. sf-header — sticky shadow, --header-h, generic mega-menu hover/focus
   --------------------------------------------------------------------------- */
class SFHeader extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    this.header = this.matches('.site-header') ? this : this.querySelector('.site-header') || this;

    // Sticky shadow
    const onScroll = () => {
      const on = window.scrollY > 4;
      this.classList.toggle('is-scrolled', on);
      if (this.header !== this) this.header.classList.toggle('is-scrolled', on);
      if (this._mega && this._mega.openRec) this._mega.close();
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();

    // Header height → --header-h
    this._lastH = 0;
    const measure = () => {
      const h = Math.round(this.header.getBoundingClientRect().height * 100) / 100;
      if (!h || h === this._lastH) return;
      this._lastH = h;
      document.documentElement.style.setProperty('--header-h', h + 'px');
      theme.pubsub.publish('header:height', { px: h });
    };
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(measure);
      this._ro.observe(this.header);
    } else {
      window.addEventListener('resize', theme.debounce(measure, 100), { signal });
    }
    measure();

    this._initMega(signal);
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    if (this._ro) this._ro.disconnect();
    if (this._mega) {
      clearTimeout(this._mega.closeTimer);
      clearTimeout(this._mega.hideTimer);
      clearTimeout(this._mega.openTimer);
    }
  }

  /** @returns {number} current header height in px */
  get height() {
    return this._lastH;
  }

  _initMega(signal) {
    const triggers = Array.from(this.querySelectorAll('.site-nav__toggle[aria-controls]'));
    const items = triggers
      .map((trigger) => ({ trigger, panel: document.getElementById(trigger.getAttribute('aria-controls')) }))
      .filter((r) => r.panel);
    if (!items.length) return;

    const host = this.querySelector('.mega-host') || items[0].panel.parentElement;
    const nav = items[0].trigger.closest('.site-nav') || this;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const mega = (this._mega = { openRec: null, closeTimer: null, hideTimer: null, openTimer: null });

    items.forEach((r) => {
      r.panel.hidden = true;
      r.trigger.setAttribute('aria-expanded', 'false');
    });
    host.hidden = true;

    const close = (mega.close = () => {
      clearTimeout(mega.closeTimer);
      clearTimeout(mega.openTimer);
      if (!mega.openRec) return;
      host.classList.remove('is-open');
      items.forEach((r) => r.trigger.setAttribute('aria-expanded', 'false'));
      clearTimeout(mega.hideTimer);
      mega.hideTimer = setTimeout(() => {
        if (!host.classList.contains('is-open')) {
          host.hidden = true;
          items.forEach((r) => (r.panel.hidden = true));
        }
      }, theme.reducedMotion ? 0 : 240);
      mega.openRec = null;
    });

    const open = (mega.open = (rec) => {
      clearTimeout(mega.closeTimer);
      clearTimeout(mega.hideTimer);
      if (mega.openRec === rec) return;
      const switching = !!mega.openRec;
      items.forEach((r) => {
        r.panel.hidden = r !== rec;
        r.trigger.setAttribute('aria-expanded', r === rec ? 'true' : 'false');
      });
      host.hidden = false;
      if (switching) {
        host.classList.add('is-open');
        rec.panel.classList.remove('mega--swap');
        void rec.panel.offsetWidth;
        rec.panel.classList.add('mega--swap');
      } else {
        clearTimeout(mega.openTimer);
        mega.openTimer = setTimeout(() => host.classList.add('is-open'), 10);
      }
      mega.openRec = rec;
    });

    const closeSoon = () => {
      clearTimeout(mega.closeTimer);
      mega.closeTimer = setTimeout(close, 180);
    };
    const cancelClose = () => clearTimeout(mega.closeTimer);

    items.forEach((rec) => {
      if (canHover) {
        rec.trigger.addEventListener(
          'pointerenter',
          (e) => {
            if (e.pointerType === 'touch') return;
            cancelClose();
            open(rec);
          },
          { signal }
        );
        rec.trigger.addEventListener('pointerleave', closeSoon, { signal });
      }
      rec.trigger.addEventListener(
        'focus',
        () => {
          cancelClose();
          open(rec);
        },
        { signal }
      );
      rec.trigger.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'ArrowDown' && mega.openRec === rec) {
            e.preventDefault();
            const first = rec.panel.querySelector(FOCUSABLE);
            if (first) safeFocus(first);
          } else if (e.key === 'Escape' && mega.openRec) {
            e.preventDefault();
            close();
          }
        },
        { signal }
      );
    });

    if (canHover) {
      host.addEventListener('pointerenter', cancelClose, { signal });
      host.addEventListener('pointerleave', closeSoon, { signal });
      nav.addEventListener('pointerleave', closeSoon, { signal });
    }

    const outside = (target) => !(nav.contains(target) || host.contains(target));

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape' || !mega.openRec) return;
        const trigger = mega.openRec.trigger;
        close();
        safeFocus(trigger);
        e.stopPropagation();
      },
      { signal, capture: true }
    );
    document.addEventListener(
      'focusin',
      (e) => {
        if (mega.openRec && outside(e.target)) close();
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (e) => {
        if (mega.openRec && outside(e.target)) close();
      },
      { signal }
    );

    if (theme.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (e) => {
          const rec = items.find((r) => r.panel.contains(e.target));
          if (rec) open(rec);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:block:deselect',
        (e) => {
          if (mega.openRec && mega.openRec.panel.contains(e.target)) close();
        },
        { signal }
      );
    }
  }
}
if (!customElements.get('sf-header')) customElements.define('sf-header', SFHeader);

/* ---------------------------------------------------------------------------
   15. sf-announcement — marquee duplication + pause on hover/focus
   --------------------------------------------------------------------------- */
class SFAnnouncement extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    this.track = this.querySelector('.announcement__track, [data-marquee-track]');
    if (!this.track) return;

    if (!this._originals) this._originals = Array.from(this.track.children).map((n) => n.cloneNode(true));

    const setPaused = (on) => {
      this.classList.toggle('is-paused', on);
      this.track.style.animationPlayState = on ? 'paused' : '';
    };
    this.addEventListener('pointerenter', () => setPaused(true), { signal });
    this.addEventListener('pointerleave', () => setPaused(false), { signal });
    this.addEventListener('focusin', () => setPaused(true), { signal });
    this.addEventListener('focusout', () => setPaused(false), { signal });

    this._lastWidth = 0;
    const rebuild = () => {
      const w = this.clientWidth;
      if (w && w !== this._lastWidth) {
        this._lastWidth = w;
        this.build();
      }
    };
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(rebuild);
      this._ro.observe(this);
    } else {
      window.addEventListener('resize', theme.debounce(rebuild, 150), { signal });
    }
    if (motionQuery && motionQuery.addEventListener) {
      motionQuery.addEventListener('change', () => this.build(), { signal });
    }
    rebuild();
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    if (this._ro) this._ro.disconnect();
  }

  /** Rebuild the track: originals + clones until content spans ≥ 2× the container. */
  build() {
    const track = this.track;
    if (!track || !this._originals) return;
    track.querySelectorAll('[data-marquee-clone]').forEach((n) => n.remove());

    const isStatic = theme.reducedMotion || theme.settings.motion.marquees === false;
    this.classList.toggle('is-static', isStatic);
    track.classList.toggle('is-static', isStatic);
    if (isStatic) return;

    const containerW = this.clientWidth;
    const kids = Array.from(track.children);
    if (!kids.length) return;
    const first = kids[0].getBoundingClientRect();
    const last = kids[kids.length - 1].getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const unitW = Math.abs(theme.rtl ? first.right - last.left : last.right - first.left) + gap;
    if (!containerW || !unitW) return;
    // k copies fill the viewport once; doubling makes translateX(-50%) loop seamlessly.
    const copies = Math.max(1, Math.ceil(containerW / unitW));
    const total = copies * 2;
    const frag = document.createDocumentFragment();
    for (let i = 1; i < total; i++) {
      this._originals.forEach((n) => {
        const c = n.cloneNode(true);
        if (c.nodeType === 1) {
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('data-marquee-clone', '');
          c.querySelectorAll('a,button,[tabindex]').forEach((f) => f.setAttribute('tabindex', '-1'));
        }
        frag.appendChild(c);
      });
    }
    track.appendChild(frag);
  }
}
if (!customElements.get('sf-announcement')) customElements.define('sf-announcement', SFAnnouncement);

/* ---------------------------------------------------------------------------
   16. sf-localization-form
   --------------------------------------------------------------------------- */
class SFLocalizationForm extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    this.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('[data-locale-code], [data-country-code]');
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();
        this.submit(btn.getAttribute('data-locale-code'), btn.getAttribute('data-country-code'));
      },
      { signal: this._ctrl.signal }
    );
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
  }

  /**
   * Set the hidden inputs and submit the enclosing localization form.
   * @param {string|null} locale iso code, e.g. 'ar'
   * @param {string|null} country iso code, e.g. 'LB'
   */
  submit(locale, country) {
    const form = this.closest('form') || this.querySelector('form');
    if (!form) return;
    const setInput = (name, value) => {
      if (value == null) return;
      let input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    };
    setInput('locale_code', locale);
    setInput('country_code', country);
    theme.pubsub.publish('locale:change', { locale, country });
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }
}
if (!customElements.get('sf-localization-form')) customElements.define('sf-localization-form', SFLocalizationForm);

/* ---------------------------------------------------------------------------
   17. sf-quantity — minus / input / plus
   --------------------------------------------------------------------------- */
class SFQuantity extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    const signal = this._ctrl.signal;
    this.input = this.querySelector('input[type="number"], input');
    if (!this.input) return;
    this.minus = this.querySelector('[data-qty="minus"], [name="minus"]');
    this.plus = this.querySelector('[data-qty="plus"], [name="plus"]');

    if (this.minus) this.minus.addEventListener('click', () => this.step(-1), { signal });
    if (this.plus) this.plus.addEventListener('click', () => this.step(1), { signal });
    this.input.addEventListener(
      'change',
      () => {
        const v = this._clamp(Number(this.input.value));
        if (String(v) !== this.input.value) this.input.value = v;
        this._sync();
      },
      { signal }
    );
    this._sync();
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
  }

  get min() {
    return !this.input || this.input.min === '' ? 1 : Number(this.input.min);
  }
  get max() {
    return !this.input || this.input.max === '' ? Infinity : Number(this.input.max);
  }
  get stepSize() {
    if (!this.input) return 1;
    return this.input.step === '' || this.input.step === 'any' ? 1 : Number(this.input.step) || 1;
  }
  /** @returns {number} */
  get value() {
    return this.input ? Number(this.input.value) || 0 : 0;
  }
  set value(v) {
    if (!this.input) return;
    this.input.value = this._clamp(Number(v));
    this._sync();
  }

  _clamp(v) {
    if (!Number.isFinite(v)) v = this.min;
    return Math.min(this.max, Math.max(this.min, v));
  }

  /**
   * Increment/decrement by `dir` steps and emit `change` on the input (bubbles).
   * @param {1|-1} dir
   */
  step(dir) {
    const next = this._clamp(this.value + dir * this.stepSize);
    if (next === this.value) return;
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _sync() {
    const v = this.value;
    if (this.minus) this.minus.disabled = v <= this.min;
    if (this.plus) this.plus.disabled = v >= this.max;
  }
}
if (!customElements.get('sf-quantity')) customElements.define('sf-quantity', SFQuantity);

/* ---------------------------------------------------------------------------
   18. Reveal + title masks
   --------------------------------------------------------------------------- */
let revealIO = null;

function revealEnabled() {
  return theme.settings.motion.reveal !== false && !theme.reducedMotion && 'IntersectionObserver' in window;
}

function getRevealIO() {
  if (!revealIO) {
    revealIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealIO.unobserve(entry.target);
        });
      },
      { threshold: 0.2 }
    );
  }
  return revealIO;
}

/**
 * Scroll reveal: `[data-reveal]` and children of `[data-reveal-group]` get `.reveal`, then
 * `.is-visible` once 20% is in view (70ms stagger inside groups). Instant when motion is off.
 * @param {ParentNode} [root=document]
 */
theme.initReveal = function initReveal(root = document) {
  const targets = new Set();
  const scope = root.querySelectorAll ? root : document;

  if (scope.matches && scope.matches('[data-reveal]')) targets.add(scope);
  scope.querySelectorAll('[data-reveal]').forEach((el) => targets.add(el));

  const groups = Array.from(scope.querySelectorAll('[data-reveal-group]'));
  if (scope.matches && scope.matches('[data-reveal-group]')) groups.unshift(scope);
  groups.forEach((group) => {
    Array.from(group.children).forEach((child, i) => {
      child.style.transitionDelay = i * STAGGER_MS + 'ms';
      targets.add(child);
    });
  });

  const enabled = revealEnabled();
  targets.forEach((el) => {
    if (el.dataset.revealReady) return;
    el.dataset.revealReady = '1';
    el.classList.add('reveal');
    if (!enabled) {
      el.classList.add('is-visible');
      return;
    }
    getRevealIO().observe(el);
  });
};

let maskIO = null;

function masksEnabled() {
  return theme.settings.motion.masks !== false && !theme.reducedMotion && 'IntersectionObserver' in window;
}

function revealMask(el) {
  el.querySelectorAll('.sh-word').forEach((w, i) => {
    w.style.transitionDelay = i * STAGGER_MS + 'ms';
    w.classList.add('is-in');
  });
}

function getMaskIO() {
  if (!maskIO) {
    maskIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          revealMask(en.target);
          maskIO.unobserve(en.target);
        });
      },
      { threshold: 0.5 }
    );
  }
  return maskIO;
}

/**
 * Title mask-up: splits `[data-mask-title]` text into `.sh-word > span` words (text only — inline
 * markup is flattened) and reveals with a 70ms stagger at 50% visibility. Skipped when disabled.
 * @param {ParentNode} [root=document]
 */
theme.initTitleMasks = function initTitleMasks(root = document) {
  if (!masksEnabled()) return;
  const scope = root.querySelectorAll ? root : document;
  const titles = Array.from(scope.querySelectorAll('[data-mask-title]'));
  if (scope.matches && scope.matches('[data-mask-title]')) titles.unshift(scope);
  titles.forEach((el) => {
    if (el.querySelector('.sh-word')) return;
    const text = el.textContent.trim();
    if (!text) return;
    /* An inline-block word is bidi-neutral, so a row of them follows the heading's own direction: an English
       heading on the Arabic storefront came out word-reversed ("Pair Your Find"), and so would a Latin brand
       name inside an Arabic heading. Words written in the other direction are therefore grouped into an
       inline run that carries that direction itself; the heading keeps its alignment. */
    const base = getComputedStyle(el).direction === 'rtl' ? 'rtl' : 'ltr';
    const dirOf = (w) => (/[֐-ࣿיִ-﷿ﹰ-﻿]/.test(w) ? 'rtl' : /[A-Za-z0-9À-ɏ]/.test(w) ? 'ltr' : null);
    el.textContent = '';
    const words = text.split(/\s+/);
    let run = null;
    words.forEach((word, i) => {
      const d = dirOf(word);
      let opened = false;
      if (d === base) run = null;
      else if (d && !run) {
        if (i > 0) el.appendChild(document.createTextNode(' '));
        run = document.createElement('span');
        run.setAttribute('dir', d);
        el.appendChild(run);
        opened = true;
      }
      /* a word with no strong letters (a dash, an ampersand) stays with the run it sits in */
      const host = run || el;
      if (i > 0 && !opened) host.appendChild(document.createTextNode(' '));
      const outer = document.createElement('span');
      outer.className = 'sh-word';
      const inner = document.createElement('span');
      inner.textContent = word;
      outer.appendChild(inner);
      host.appendChild(outer);
    });
    el.classList.add('has-mask');
    getMaskIO().observe(el);
    /* fail-safe: never leave an in-view title hidden if the observer is throttled (background tab, hidden pane) */
    setTimeout(() => {
      if (!el.isConnected || el.querySelector('.sh-word.is-in')) return;
      const r = el.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) {
        el.querySelectorAll('.sh-word').forEach((w, i) => { w.style.transitionDelay = (i * STAGGER_MS) + 'ms'; w.classList.add('is-in'); });
      }
    }, 1500);
  });
};

/* ---------------------------------------------------------------------------
   19. sf-cart-count
   --------------------------------------------------------------------------- */
class SFCartCount extends HTMLElement {
  connectedCallback() {
    this._unsub = theme.pubsub.subscribe('cart:updated', ({ cart }) => {
      if (cart && typeof cart.item_count === 'number') this.set(cart.item_count);
    });
  }
  disconnectedCallback() {
    if (this._unsub) this._unsub();
  }
  /** @param {number} n */
  set(n) {
    const count = Number(n) || 0;
    this.textContent = String(count);
    this.hidden = count === 0;
    this.dataset.count = String(count);
  }
}
class SFFavouritesCount extends HTMLElement {
  connectedCallback() {
    this._render();
    this._unsub = theme.pubsub.subscribe('favourites:change', () => this._render());
  }
  disconnectedCallback() {
    if (this._unsub) this._unsub();
  }
  _render() {
    const n = theme.favourites && typeof theme.favourites.count === 'function' ? theme.favourites.count() : 0;
    this.textContent = String(n);
    this.hidden = n === 0;
  }
}
if (!customElements.get('sf-favourites-count')) customElements.define('sf-favourites-count', SFFavouritesCount);

if (!customElements.get('sf-cart-count')) customElements.define('sf-cart-count', SFCartCount);

/* ---------------------------------------------------------------------------
   20. Card action elements
   --------------------------------------------------------------------------- */
function cardMeta(el) {
  const own = el.dataset;
  const card = el.closest('.card-b, .drop-card, [data-product-handle]');
  const q = (sel) => (card ? card.querySelector(sel) : null);
  const text = (node) => (node ? node.textContent.trim() : '');
  const img = q('img');
  return {
    handle: own.handle || (card && (card.dataset.handle || card.dataset.productHandle)) || '',
    title: own.title || text(q('.card-b__name, .drop-card__name')) || '',
    vendor: own.vendor || text(q('.card-b__brand, .drop-card__brand')) || '',
    url: own.url || (card && card.dataset.url) || '',
    image: own.image || (img ? img.currentSrc || img.src : '') || '',
    price: own.price || text(q('.price__amount, .card-b__price')) || ''
  };
}

/** Shared base for wrapper elements that enhance an inner <button> (or act as one). */
class SFActionBase extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    this.button = this.querySelector('button') || this;
    if (this.button === this && !this.hasAttribute('role')) {
      this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.activate(e);
          }
        },
        { signal: this._ctrl.signal }
      );
    }
    this.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        this.activate(e);
      },
      { signal: this._ctrl.signal }
    );
    if (!this.dataset.handle) {
      const h = cardMeta(this).handle;
      if (h) this.dataset.handle = h;
    }
    this.ready();
  }
  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    this.teardown();
  }
  ready() {}
  teardown() {}
  activate() {}
  setBusy(on) {
    this.classList.toggle('is-loading', on);
    this.button.classList.toggle('is-loading', on);
    if (on) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
  }
}

class SFFavouriteButton extends SFActionBase {
  ready() {
    this.setSaved(theme.favourites.has(this.dataset.handle));
  }
  /** @param {boolean} on */
  setSaved(on) {
    this.classList.toggle('is-saved', !!on);
    this.button.classList.toggle('is-saved', !!on);
    this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    /* the accessible name has to follow the state, not just aria-pressed */
    const label = on ? this.button.dataset.labelSaved : this.button.dataset.labelSave;
    if (label) {
      this.button.setAttribute('aria-label', label);
      const t = this.button.querySelector('[data-fav-label]');
      if (t) t.textContent = label;
    }
  }
  activate() {
    const meta = cardMeta(this);
    if (!meta.handle) return;
    theme.favourites.toggle(meta.handle, meta);
  }
}
if (!customElements.get('sf-favourite-button')) customElements.define('sf-favourite-button', SFFavouriteButton);

class SFQuickAddButton extends SFActionBase {
  activate() {
    const handle = this.dataset.handle || cardMeta(this).handle;
    if (!handle) return;
    theme.quickAdd.open(handle, this.button, { url: this.dataset.url || cardMeta(this).url });
  }
}
if (!customElements.get('sf-quick-add-button')) customElements.define('sf-quick-add-button', SFQuickAddButton);

class SFSizesToggle extends SFActionBase {
  ready() {
    this.button.setAttribute('aria-expanded', this.classList.contains('is-open') ? 'true' : 'false');
  }
  get strip() {
    const card = this.closest('.card-b, .drop-card');
    return card ? card.querySelector('.card-sizes') : null;
  }
  /** @param {boolean} on */
  setOpen(on) {
    const strip = this.strip;
    if (!strip) return;
    /* [hidden] is the no-JS state; once JS runs the strip animates between .is-open and its resting state */
    if (strip.hidden) {
      strip.hidden = false;
      strip.offsetWidth; // flush, so the first open animates instead of snapping
    }
    strip.classList.toggle('is-open', on);
    strip.setAttribute('aria-hidden', on ? 'false' : 'true');
    if (on) this._edges(strip);
    this.classList.toggle('is-open', on);
    this.button.classList.toggle('is-open', on);
    this.button.setAttribute('aria-expanded', on ? 'true' : 'false');
    this._unbindDoc();
    if (on) {
      /* Each card owns its panel: no outside-click collapse, so several products can stay open at once while
         you compare them. Escape still closes THIS panel, but only while focus is inside this card. */
      const card = this.closest('.card-b, .drop-card') || this;
      this._onDocKey = (e) => {
        if (e.key !== 'Escape' || e.defaultPrevented) return;
        if (!card.contains(document.activeElement)) return;
        e.stopPropagation();
        this.setOpen(false);
        safeFocus(this.button);
      };
      document.addEventListener('keydown', this._onDocKey, true);
    }
  }
  /* the strip's edge fade shows only while chips sit behind it (more to scroll to) */
  _edges(strip) {
    const upd = () => {
      const room = strip.scrollWidth - strip.clientWidth;
      strip.classList.toggle('is-overflowing', room > 2);
      strip.classList.toggle('is-at-end', room <= 2 || Math.abs(strip.scrollLeft) >= room - 2);
    };
    if (!strip._edgesBound) {
      strip._edgesBound = true;
      strip.addEventListener('scroll', upd, { passive: true });
    }
    upd();
  }
  _unbindDoc() {
    if (this._onDocKey) document.removeEventListener('keydown', this._onDocKey, true);
    this._onDocKey = null;
  }
  teardown() {
    this._unbindDoc();
  }
  activate() {
    /* toggle this card only — opening product B must not close product A */
    this.setOpen(!this.classList.contains('is-open'));
  }
}
if (!customElements.get('sf-sizes-toggle')) customElements.define('sf-sizes-toggle', SFSizesToggle);

/* A size chip in a card's sizes strip (snippets/size-list.liquid) opens quick add on THAT size, in the colourway the
   card shows (its quick-add url carries ?variant= once a colour is picked). The variant is matched from the
   product's own JSON by option values, preferring one that can be bought; if that fails quick add simply opens on
   the product's default, as the bag button does. */
const cardProductJson = new Map();
document.addEventListener('click', async (e) => {
  const pill = e.target && e.target.closest ? e.target.closest('.card-sizes .size-pill') : null;
  if (!pill || pill.disabled) return;
  e.preventDefault();
  const card = pill.closest('.card-b, .drop-card');
  if (!card) return;
  const meta = cardMeta(pill);
  const qa = card.querySelector('sf-quick-add-button');
  const url = (qa && qa.dataset.url) || meta.url || '';
  const handle = (qa && qa.dataset.handle) || meta.handle;
  if (!handle) return;
  const size = (pill.dataset.sizeValue || pill.textContent || '').trim();
  let variant = '';
  try {
    if (!cardProductJson.has(handle)) {
      cardProductJson.set(handle, fetch(rootPrefix() + '/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    const product = await cardProductJson.get(handle);
    if (product && Array.isArray(product.variants)) {
      const names = (product.options || []).map((o) => String((o && o.name) || o || '').toLowerCase());
      const sizeAt = product.variants[0] ? product.variants[0].options.findIndex((_, i) => product.variants.some((v) => v.options[i] === size)) : -1;
      const colourAt = names.findIndex((n) => n.indexOf('colo') > -1);
      let colour = '';
      const q = url.indexOf('?');
      const current = q > -1 ? new URLSearchParams(url.slice(q + 1)).get('variant') : '';
      if (current && colourAt > -1) {
        const cv = product.variants.find((v) => String(v.id) === String(current));
        if (cv) colour = cv.options[colourAt];
      }
      if (sizeAt > -1) {
        const fits = product.variants.filter((v) => v.options[sizeAt] === size && (!colour || colourAt < 0 || v.options[colourAt] === colour));
        const pick = fits.find((v) => v.available) || fits[0];
        if (pick) variant = String(pick.id);
      }
    }
  } catch (err) { /* open on the default variant */ }
  theme.quickAdd.open(handle, pill, { url, variant });
});

class SFRemindButton extends SFActionBase {
  ready() {
    const list = theme.storage.get('reminders', []);
    const set = Array.isArray(list) && list.some((r) => r && r.handle === this.dataset.handle);
    this.setSet(set);
    this._unsub = theme.pubsub.subscribe('remind:saved', ({ handle }) => {
      if (handle === this.dataset.handle) this.setSet(true);
    });
  }
  teardown() {
    if (this._unsub) this._unsub();
  }
  /** @param {boolean} on */
  setSet(on) {
    this.classList.toggle('is-set', !!on);
    this.button.classList.toggle('is-set', !!on);
    this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  activate() {
    const sheet = document.getElementById('notify-sheet');
    const meta = cardMeta(this);
    const card = this.closest('[data-release]');
    const detail = {
      handle: this.dataset.handle || meta.handle,
      title: this.dataset.title || meta.title,
      release: this.dataset.release || (card ? card.dataset.release : '') || '',
      image: this.dataset.image || meta.image,
      url: this.dataset.url || meta.url,
      vendor: meta.vendor,
      opener: this.button
    };
    if (!sheet || typeof sheet.open !== 'function') {
      console.warn('[theme] sf-remind-button: #notify-sheet is not on the page');
      return;
    }
    document.dispatchEvent(new CustomEvent('remind:open', { bubbles: true, detail }));
    theme.pubsub.publish('remind:open', detail);
    sheet.open(this.button);
  }
}
if (!customElements.get('sf-remind-button')) customElements.define('sf-remind-button', SFRemindButton);

class SFShareButton extends SFActionBase {
  async activate() {
    const url = this.dataset.url || window.location.href;
    const data = { url };
    if (this.dataset.title) data.title = this.dataset.title;
    if (this.dataset.text) data.text = this.dataset.text;
    if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
      try {
        await navigator.share(data);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      theme.toast(theme.strings.copied, { type: 'success' });
    } catch (e) {
      theme.toast(theme.strings.error_generic, { type: 'error' });
    }
  }
}
if (!customElements.get('sf-share-button')) customElements.define('sf-share-button', SFShareButton);

/* ---------------------------------------------------------------------------
   21. sf-countdown — data-release (ISO or epoch) → dd/hh/mm(/ss)
   --------------------------------------------------------------------------- */
function parseRelease(value) {
  if (value == null || value === '') return NaN;
  const s = String(value).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n < 1e12 ? n * 1000 : n;
  }
  return Date.parse(s);
}

class SFCountdown extends HTMLElement {
  connectedCallback() {
    this._ctrl = new AbortController();
    this.release = parseRelease(this.dataset.release);
    this.fields = {
      d: this.querySelector('[data-cd-d]'),
      h: this.querySelector('[data-cd-h]'),
      m: this.querySelector('[data-cd-m]'),
      s: this.querySelector('[data-cd-s]')
    };
    if (!Number.isFinite(this.release)) return;
    document.addEventListener('visibilitychange', () => !document.hidden && this.tick(), {
      signal: this._ctrl.signal
    });
    this.tick();
    this._timer = setInterval(() => this.tick(), this.fields.s ? 1000 : 30000);
  }

  disconnectedCallback() {
    if (this._ctrl) this._ctrl.abort();
    clearInterval(this._timer);
  }

  /** Recompute and render the remaining time; adds `is-live` at zero. */
  tick() {
    const diff = this.release - Date.now();
    const pad = (n) => String(Math.max(0, n)).padStart(2, '0');
    if (diff <= 0) {
      Object.values(this.fields).forEach((f) => f && (f.textContent = '00'));
      if (!this.classList.contains('is-live')) {
        this.classList.add('is-live');
        this.dispatchEvent(new CustomEvent('countdown:live', { bubbles: true, detail: { release: this.release } }));
      }
      clearInterval(this._timer);
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    if (this.fields.d) this.fields.d.textContent = pad(d);
    if (this.fields.h) this.fields.h.textContent = pad(h);
    if (this.fields.m) this.fields.m.textContent = pad(m);
    if (this.fields.s) this.fields.s.textContent = pad(s);
  }
}
if (!customElements.get('sf-countdown')) customElements.define('sf-countdown', SFCountdown);

/* ---------------------------------------------------------------------------
   22. Quick add — fetch /products/<handle>?section_id=quick-add into #quick-add-drawer
   --------------------------------------------------------------------------- */
const quickAddState = { seq: 0, ctrl: null };

function quickAddSkeleton() {
  const wrap = document.createElement('div');
  wrap.className = 'skeleton skeleton--quick-add';
  wrap.setAttribute('aria-busy', 'true');
  wrap.setAttribute('aria-label', theme.strings.loading);
  ['skeleton__media', 'skeleton__line', 'skeleton__line skeleton__line--short', 'skeleton__pills', 'skeleton__btn'].forEach(
    (cls) => {
      const el = document.createElement('div');
      el.className = cls;
      wrap.appendChild(el);
    }
  );
  return wrap;
}

theme.quickAdd = {
  /**
   * Open the quick-add drawer for a product handle. Falls back to navigating to the
   * product page when `#quick-add-drawer` is not on the page.
   * @param {string} handle
   * @param {Element} [opener]
   * @param {{url?:string}} [opts]
   * @returns {Promise<Element|null>} the injected `.quick-add` root (null on fallback/error)
   */
  /** Closing, or opening a different product, invalidates whatever is in flight: a late response must not
   *  land in a closed drawer or show the previous product. */
  cancel() {
    quickAddState.seq += 1;
    if (quickAddState.ctrl) { quickAddState.ctrl.abort(); quickAddState.ctrl = null; }
  },
  async open(handle, opener, opts = {}) {
    if (!handle) return null;
    const productUrl = opts.url || rootPrefix() + '/products/' + encodeURIComponent(handle);
    /* a card that has had a colour picked carries the variant on its url; the drawer must open on that
       colourway, not on the product's default, or the shopper adds a colour they did not choose */
    let variantId = opts.variant || '';
    if (!variantId && opts.url) {
      const q = String(opts.url).indexOf('?');
      if (q > -1) variantId = new URLSearchParams(String(opts.url).slice(q + 1)).get('variant') || '';
    }
    const drawer = document.getElementById('quick-add-drawer');
    const body = drawer && drawer.querySelector('.drawer__body');
    if (!drawer || typeof drawer.open !== 'function' || !body) {
      window.location.href = productUrl;
      return null;
    }

    const id = ++quickAddState.seq;
    if (quickAddState.ctrl) quickAddState.ctrl.abort();
    const ctrl = (quickAddState.ctrl = new AbortController());

    body.replaceChildren(quickAddSkeleton());
    body.setAttribute('aria-busy', 'true');
    drawer.dataset.handle = handle;
    const button = opener && opener.closest ? opener.closest('sf-quick-add-button') : null;
    if (button && typeof button.setBusy === 'function') button.setBusy(true);
    drawer.open(opener);

    try {
      const query = { section_id: 'quick-add' };
      if (variantId) query.variant = variantId;
      const url = withQuery(rootPrefix() + '/products/' + encodeURIComponent(handle), query);
      const html = await theme.fetchText(url, { signal: ctrl.signal, headers: { Accept: 'text/html' } });
      if (id !== quickAddState.seq) return null; // stale
      const content = theme.sectionHtml(html, 'quick-add', { inner: true });
      if (!content) throw new Error('quick-add: empty section');
      body.replaceChildren(content);
      body.removeAttribute('aria-busy');
      const initial = body.querySelector('[data-quick-add-initial]');
      if (initial) safeFocus(initial);
      theme.pubsub.publish('quickadd:open', { handle, drawer, element: content, opener });
      drawer.dispatchEvent(new CustomEvent('quickadd:open', { bubbles: true, detail: { handle, element: content } }));
      return content;
    } catch (err) {
      if (err && err.name === 'AbortError') return null;
      if (id !== quickAddState.seq) return null;
      body.removeAttribute('aria-busy');
      const p = document.createElement('p');
      p.className = 'quick-add__error';
      p.setAttribute('role', 'alert');
      p.textContent = theme.strings.error_generic;
      const a = document.createElement('a');
      a.className = 'btn btn--secondary btn--full';
      a.href = productUrl;
      a.textContent = theme.strings.view_product || theme.strings.learn_more || theme.strings.error_generic;
      body.replaceChildren(p, a);
      theme.toast(theme.strings.error_generic, { type: 'error' });
      return null;
    } finally {
      if (button && typeof button.setBusy === 'function' && id === quickAddState.seq) button.setBusy(false);
    }
  },

  /** Close the quick-add drawer if open. */
  close() {
    const drawer = document.getElementById('quick-add-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }
};

/* ---------------------------------------------------------------------------
   23. Boot + theme editor lifecycle
   --------------------------------------------------------------------------- */
function boot() {
  syncMotionClass();
  theme.initReveal(document);
  theme.initTitleMasks(document);
  theme.pubsub.publish('theme:ready', { theme });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

document.addEventListener('shopify:section:load', (e) => {
  const root = e.target || document;
  theme.initReveal(root);
  theme.initTitleMasks(root);
});
}

/* --- fit note disclosure (BUILD r) --- */
(function () {
  'use strict';
  /* A real button plus aria-expanded, so the chevron direction and the announced state can never disagree.
     Delegated, so a section re-render (variant change) needs no re-binding. */
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-fit-toggle]') : null;
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.hidden = open;
  });
})();

/* --- quick-add: the thumbnail and the links out of the drawer follow its colourway --- */
(function () {
  'use strict';
  /* sections/quick-add.liquid renders the thumbnail and the three links out of the drawer (thumbnail, name,
     "View full details") for the variant the drawer was opened on. product-form.js re-renders the picker,
     price and buttons when the shopper changes colour in the drawer, but not these four, so without this
     they would keep showing and linking to the colourway the drawer was OPENED on. Its variant:change event
     carries the new variant's JSON (the section's data-selected-variant), which is all they need; a variant
     with no photo of its own falls back to the product's, as the section itself does. */
  const THUMB_WIDTHS = [84, 168, 252]; /* the widths sections/quick-add.liquid asks the image snippet for */
  const sized = (src, w) => {
    try {
      const u = new URL(src, window.location.href);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (e) {
      return '';
    }
  };
  document.addEventListener('variant:change', (e) => {
    const panel = e.target && e.target.closest ? e.target.closest('.quick-add') : null;
    const variant = e.detail && e.detail.variant;
    if (!panel || !variant || !variant.id) return;
    const base = (panel.dataset.url || '').split('?')[0];
    if (base) {
      const url = base + '?variant=' + encodeURIComponent(variant.id);
      panel.querySelectorAll('a.quick-add__thumb, a.quick-add__link, a.quick-add__more').forEach((a) => a.setAttribute('href', url));
    }
    const thumb = panel.querySelector('.quick-add__thumb');
    const img = thumb && thumb.querySelector('img');
    if (!img) return;
    const media = variant.featured_media && variant.featured_media.preview_image;
    const src = (media && media.src) || (variant.featured_image && variant.featured_image.src) || thumb.dataset.productImage || '';
    if (!src || !sized(src, 1)) return;
    img.setAttribute('srcset', THUMB_WIDTHS.map((w) => sized(src, w) + ' ' + w + 'w').join(', '));
    img.setAttribute('src', sized(src, THUMB_WIDTHS[THUMB_WIDTHS.length - 1]));
  });
})();

/* --- quick-add: drop stale loads on close (BUILD t) --- */
(function () {
  'use strict';
  document.addEventListener('drawer:close', (e) => {
    const d = e.target;
    if (!d || d.id !== 'quick-add-drawer') return;
    if (theme.quickAdd && typeof theme.quickAdd.cancel === 'function') theme.quickAdd.cancel();
    const body = d.querySelector('.drawer__body');
    if (body) body.removeAttribute('aria-busy');
  });
})();

/* --- in-stock collection links (BUILD bm) ---
   With sold-out products hidden (Theme settings › Filters; body[data-hide-sold-out]) a collection is listed with
   Shopify's in-stock filter, filter.v.availability=1. A link to a collection gets it the moment it is pointed at,
   focused or touched — before the browser's hover prefetch or the page transition reads it — so the visit lands
   straight on the in-stock list instead of passing through the redirect in layout/theme.liquid. The rendered HTML
   stays as it is, for crawlers. Other parameters keep their own encoding; a ?view= listing is left alone. */
(function () {
  'use strict';
  if (!document.body || !document.body.hasAttribute('data-hide-sold-out')) return;
  const LISTING = /^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\/[^/]+\/?$/i;
  const tag = (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.hasAttribute('download')) return;
    let u;
    try { u = new URL(a.href, location.href); } catch (err) { return; }
    if (u.origin !== location.origin || !LISTING.test(u.pathname)) return;
    const q = u.search.replace(/^\?/, '');
    if (/(^|&)filter\.v\.availability=/.test(q) || /(^|&)view=/.test(q)) return;
    a.setAttribute('href', u.pathname + '?' + (q ? q + '&' : '') + 'filter.v.availability=1' + u.hash);
  };
  ['pointerover', 'focusin', 'touchstart', 'mousedown', 'click'].forEach((type) => {
    document.addEventListener(type, tag, { capture: true, passive: true });
  });
})();
